import React, { useState, useEffect, useCallback, forwardRef } from "react";
import { StyleSheet, Platform, TouchableOpacity } from "react-native";
import * as ExpoImagePicker from "expo-image-picker";
import * as ImageManipulator from "expo-image-manipulator";
import Icon from "@components/atoms/Icon";
import Colors from "@constants/Colors";
import Loading from "@components/atoms/Loading";
import _ from "lodash";
import { isVideo, getExtensionFromType } from "@lib/util/file";
import { resolveError } from "@lib/util/error";
import { appVersion } from "@constants/Env";
import * as Device from "expo-device";
import {
  validateVideoFileOnWeb,
  collectErrorDetails,
  createRNFileWithFallback,
  safeNotify,
  dataURItoBlob,
  fetchBlobWithFallback,
  convertToMB,
  isSupportedMimeType,
  supportMimeTypes,
} from "./ImagePicker/helpers";
import { handleWebFilePickCore } from "./ImagePicker/webHandlers";

type Props = {
  /** 画像が選択されたときに発火するイベントハンドラ */
  onChange: (e: ImageFile | ImageFile[]) => void;
  /** エラーハンドラ */
  onError: (e: string) => void;
  /** 選択開始 */
  onPickStart?: () => void;
  /** 選択完了 */
  onPickEnd?: () => void;
  /** 選択した画像のリサイズ後のサイズ。スクエアにcrop & resize されます。 */
  resizeSize?: number;
  loading?: boolean;
  /** 複数選択するかどうか */
  allowsMultipleSelection?: boolean;
  /** 複数選択の場合の最大選択数 */
  selectionLimit?: number;
  /** 画像/動画 選択のタイプ Image/Video/All */
  pickupMediaType?: ExpoImagePicker.MediaTypeOptions;
  /** 動画の上限容量(mb) */
  maxVideoSize?: number;
  /** 画像の上限容量(mb) */
  maxImageSize?: number;
};

type ResizeOption = {
  uri: string;
  width?: number;
  height?: number;
};

export type ImageFile = {
  uri: string;
  file: File;
};

const ImagePicker = forwardRef<HTMLDivElement, Props>(
  (
    {
      onChange,
      onError,
      onPickStart = () => {},
      onPickEnd = () => {},
      resizeSize = 800,
      loading = false,
      allowsMultipleSelection = false,
      selectionLimit = 0,
      pickupMediaType = ExpoImagePicker.MediaTypeOptions.Images,
      maxVideoSize = 400,
      maxImageSize = 10,
    }: Props,
    ref
  ): JSX.Element => {
    const [processing, setProcessing] = useState<boolean>(false);
    const fileInputRef = React.useRef<HTMLInputElement>(null);

    const resize = useCallback(
      async ({
        uri,
        width,
        height,
      }: ResizeOption): Promise<ImageManipulator.ImageResult> => {
        const resizeOption = (): {
          width?: number | undefined;
          height?: number | undefined;
        } => {
          if (width != null && height == null) {
            return { width };
          }
          if (width == null && height != null) {
            return { height };
          }
          return { width, height };
        };

        const result = await ImageManipulator.manipulateAsync(
          uri,
          [
            {
              resize: resizeOption(),
            },
          ],
          {
            compress: 1,
            format: ImageManipulator.SaveFormat.JPEG,
            base64: false,
          }
        );
        return result;
      },
      []
    );

    const pickupImage =
      useCallback(async (): Promise<ExpoImagePicker.ImagePickerResult> => {
        const result = await ExpoImagePicker.launchImageLibraryAsync({
          mediaTypes: pickupMediaType,
          allowsEditing: Platform.OS === "ios" && !allowsMultipleSelection,
          allowsMultipleSelection,
          selectionLimit,
          aspect: [1, 1],
          base64: true,
          quality: 1,
          orderedSelection: allowsMultipleSelection,
        });
        return result;
      }, [allowsMultipleSelection, pickupMediaType, selectionLimit]);

    const pick = useCallback(async () => {
      try {
        const res = await pickupImage();
        if (res.canceled) {
          // キャンセルログ
          safeNotify(
            {
              type: "ImagePickerInfo: SingleImagePickCanceled",
              searchableType: "IMAGE_UPLOAD_CANCELED_Single",
              context: {
                platform: Platform.OS,
                pickupMediaType,
                maxImageSize,
                deviceInfo: {
                  appVersion,
                  osVersion: Device.osVersion,
                },
              },
              timestamp: new Date().toISOString(),
            },
            { level: "info" }
          );
          return;
        }

        if (Platform.OS === "web") {
          onPickStart();
        }

        const asset = _.head(res.assets);
        if (asset === undefined) {
          return;
        }

        const fileSize = convertToMB(asset.fileSize);
        if (fileSize !== null && fileSize > maxImageSize) {
          // 画像サイズエラーログ
          safeNotify(
            {
              type: "ImagePickerError: ImageSizeExceeded",
              searchableType: "IMAGE_UPLOAD_ERROR_SizeExceeded",
              context: {
                platform: Platform.OS,
                fileInfo: {
                  actualSizeMB: fileSize,
                  limitMB: maxImageSize,
                  exceedByMB: Number((fileSize - maxImageSize).toFixed(2)),
                  fileName: asset.fileName,
                  width: asset.width,
                  height: asset.height,
                },
                deviceInfo: {
                  appVersion,
                  osVersion: Device.osVersion,
                },
              },
              timestamp: new Date().toISOString(),
            },
            { level: "warning" }
          );

          throw new Error(
            `${fileSize}MBの写真が選択されました。${maxImageSize}MB以下に圧縮して選択してください。`
          );
        }

        const result =
          asset.width > asset.height
            ? await resize({ uri: asset.uri, width: resizeSize })
            : await resize({ uri: asset.uri, height: resizeSize });

        const { uri } = result;

        // Web環境ではBlobに変換、React Native環境ではフォールバック機構付きで処理
        if (Platform.OS === "web") {
          let blob: Blob;

          // data URIの場合は同期的変換、blob URLの場合はfetchBlobWithFallbackを使用
          if (uri.startsWith("data:")) {
            // eslint-disable-next-line no-console
            console.log("Detected data URI, using synchronous conversion");
            blob = dataURItoBlob(uri);
          } else {
            // blob URL等の場合、fetch()を試行し、失敗時はXMLHttpRequestにフォールバック
            blob = await fetchBlobWithFallback(uri);
          }

          const extension = getExtensionFromType(blob.type);
          const file = new File([blob], `${Date.now()}.${extension}`, {
            type: blob.type,
          });

          if (!isSupportedMimeType(file.type)) {
            // MIMEタイプエラーログ
            safeNotify(
              {
                type: "ImagePickerError: UnsupportedMimeType",
                searchableType: "IMAGE_UPLOAD_ERROR_UnsupportedMime",
                context: {
                  platform: Platform.OS,
                  fileInfo: {
                    actualMimeType: file.type,
                    fileName: file.name,
                    size: file.size,
                    sizeMB: Number((file.size / (1024 * 1024)).toFixed(2)),
                  },
                  validation: {
                    pickupMediaType,
                    supportedTypes: supportMimeTypes(pickupMediaType),
                  },
                  deviceInfo: {
                    appVersion,
                    osVersion: Device.osVersion,
                  },
                },
                timestamp: new Date().toISOString(),
              },
              { level: "warning" }
            );

            throw new Error(
              `選択可能なファイルは${supportMimeTypes(pickupMediaType).join(
                ", "
              )}のいずれかです。`
            );
          }

          // 成功ログ（Web環境）
          safeNotify(
            {
              type: "ImagePickerSuccess: SingleImagePick",
              searchableType: "IMAGE_UPLOAD_SUCCESS_Single",
              context: {
                platform: Platform.OS,
                fileInfo: {
                  size: file.size,
                  sizeMB: Number((file.size / (1024 * 1024)).toFixed(2)),
                  type: file.type,
                  name: file.name,
                },
                processing: {
                  isDataURI: uri.startsWith("data:"),
                  originalUriPrefix: `${uri.substring(0, 50)}...`,
                },
                deviceInfo: {
                  appVersion,
                  osVersion: Device.osVersion,
                  modelName: Device.modelName,
                },
              },
              timestamp: new Date().toISOString(),
            },
            { level: "info" }
          );

          onChange({ uri, file });
        } else {
          // React Native環境: フォールバック機構付きでファイル作成
          const fileResult = await createRNFileWithFallback(uri);
          onChange(fileResult);
        }
      } catch (e: unknown) {
        try {
          // Slack通知（失敗しても既存エラーハンドリングは継続）
          const errorDetails = collectErrorDetails(e, {
            operation: "SingleImagePick",
            additionalInfo: {
              pickupMediaType,
              maxImageSize,
              resizeSize,
            },
          });

          safeNotify({
            type: "ImagePickerError: SingleImagePick",
            searchableType: "iOS_IMAGE_UPLOAD_ERROR_SingleImagePick",
            ...errorDetails,
          });
        } catch (notificationError) {
          // Slack通知が失敗しても既存動作を継続
          // eslint-disable-next-line no-console
          console.warn(
            "SingleImagePick notification failed:",
            notificationError
          );
        }

        onError(resolveError(e).message);
      } finally {
        onPickEnd();
      }
    }, [
      maxImageSize,
      onChange,
      onError,
      onPickEnd,
      onPickStart,
      pickupImage,
      pickupMediaType,
      resize,
      resizeSize,
    ]);

    const multiPick = useCallback(async () => {
      try {
        const res = await pickupImage();
        if (res.canceled) {
          // キャンセルログ
          safeNotify(
            {
              type: "ImagePickerInfo: MultiImagePickCanceled",
              searchableType: "IMAGE_UPLOAD_CANCELED_Multi",
              context: {
                platform: Platform.OS,
                pickupMediaType,
                maxImageSize,
                maxVideoSize,
                selectionLimit,
                deviceInfo: {
                  appVersion,
                  osVersion: Device.osVersion,
                },
              },
              timestamp: new Date().toISOString(),
            },
            { level: "info" }
          );
          return;
        }

        if (Platform.OS === "web") {
          onPickStart();
        }

        // Webプラットフォームで非MP4動画ファイルを早期にチェック
        // これはMP4ファイルには影響しません - isNonMp4Videoで明示的に許可されています
        res.assets.forEach(validateVideoFileOnWeb);

        // 動画バリデーション通過ログ（QuickTime等のMP4互換ファイルの追跡用）
        const videoAssets = res.assets.filter((asset) =>
          isVideo({ uri: asset.uri })
        );
        if (Platform.OS === "web" && videoAssets.length > 0) {
          safeNotify(
            {
              type: "ImagePickerInfo: VideoValidationPassed",
              searchableType: "IMAGE_UPLOAD_INFO_VideoValidationPassed",
              context: {
                platform: Platform.OS,
                videosInfo: {
                  totalVideos: videoAssets.length,
                  videos: videoAssets.map((asset, idx) => ({
                    index: idx,
                    fileName: asset.fileName,
                    fileSize: asset.fileSize,
                    sizeMB: convertToMB(asset.fileSize),
                    width: asset.width,
                    height: asset.height,
                    type: asset.type,
                    duration: asset.duration,
                  })),
                },
                validation: {
                  maxVideoSize,
                  note: "QuickTime container files with H.264/H.265 codecs are allowed",
                },
                deviceInfo: {
                  appVersion,
                  osVersion: Device.osVersion,
                },
              },
              timestamp: new Date().toISOString(),
            },
            { level: "info" }
          );
        }

        // 並行処理を逐次処理に変更してファイルハンドル競合を防ぐ（安全に実行）
        const resizeUriList: string[] = [];

        try {
          // 逐次処理での画像処理（ファイルハンドル競合回避のため）
          // eslint-disable-next-line no-await-in-loop
          for (let index = 0; index < res.assets.length; index += 1) {
            const asset = res.assets[index];
            const fileSize = convertToMB(asset.fileSize);

            // eslint-disable-next-line no-console
            console.log(`Processing image ${index + 1}/${res.assets.length}`, {
              uri: asset.uri,
              fileName: asset.fileName,
              fileSize,
              width: asset.width,
              height: asset.height,
              type: asset.type,
            });

            try {
              if (isVideo({ uri: asset.uri })) {
                if (fileSize !== null && fileSize > maxVideoSize) {
                  // 動画サイズエラーログ
                  safeNotify(
                    {
                      type: "ImagePickerError: VideoSizeExceeded",
                      searchableType: "IMAGE_UPLOAD_ERROR_VideoSizeExceeded",
                      context: {
                        platform: Platform.OS,
                        fileInfo: {
                          actualSizeMB: fileSize,
                          limitMB: maxVideoSize,
                          exceedByMB: Number(
                            (fileSize - maxVideoSize).toFixed(2)
                          ),
                          fileName: asset.fileName,
                          width: asset.width,
                          height: asset.height,
                        },
                        processingInfo: {
                          assetIndex: index,
                          totalAssets: res.assets.length,
                        },
                        deviceInfo: {
                          appVersion,
                          osVersion: Device.osVersion,
                        },
                      },
                      timestamp: new Date().toISOString(),
                    },
                    { level: "warning" }
                  );

                  throw new Error(
                    `${fileSize}MBの動画が選択されました。${maxVideoSize}MB以下に圧縮して選択してください。`
                  );
                }
                // eslint-disable-next-line no-console
                console.log(`Video asset processed: ${asset.uri}`);
                resizeUriList.push(asset.uri);
              } else {
                if (fileSize !== null && fileSize > maxImageSize) {
                  // 画像サイズエラーログ
                  safeNotify(
                    {
                      type: "ImagePickerError: ImageSizeExceeded",
                      searchableType: "IMAGE_UPLOAD_ERROR_ImageSizeExceeded",
                      context: {
                        platform: Platform.OS,
                        fileInfo: {
                          actualSizeMB: fileSize,
                          limitMB: maxImageSize,
                          exceedByMB: Number(
                            (fileSize - maxImageSize).toFixed(2)
                          ),
                          fileName: asset.fileName,
                          width: asset.width,
                          height: asset.height,
                        },
                        processingInfo: {
                          assetIndex: index,
                          totalAssets: res.assets.length,
                        },
                        deviceInfo: {
                          appVersion,
                          osVersion: Device.osVersion,
                        },
                      },
                      timestamp: new Date().toISOString(),
                    },
                    { level: "warning" }
                  );

                  throw new Error(
                    `${fileSize}MBの写真が選択されました。${maxImageSize}MB以下に圧縮して選択してください。`
                  );
                }

                // eslint-disable-next-line no-console
                console.log(`Starting image resize for asset ${index + 1}`);
                const resized =
                  asset.width > asset.height
                    ? // eslint-disable-next-line no-await-in-loop
                      await resize({ uri: asset.uri, width: resizeSize })
                    : // eslint-disable-next-line no-await-in-loop
                      await resize({ uri: asset.uri, height: resizeSize });

                resizeUriList.push(resized.uri);
                // eslint-disable-next-line no-console
                console.log(
                  `Image resize completed for asset ${index + 1}: ${
                    resized.uri
                  }`
                );
              }
            } catch (error) {
              // eslint-disable-next-line no-console
              console.error(`Error processing asset ${index + 1}:`, error);

              try {
                // 詳細なSlack通知（失敗してもエラー処理は継続）
                const errorDetails = collectErrorDetails(error, {
                  operation: "AssetProcessing",
                  assetInfo: {
                    uri: asset.uri,
                    fileName: asset.fileName,
                    fileSize: asset.fileSize,
                    width: asset.width,
                    height: asset.height,
                    type: asset.type,
                  },
                  additionalInfo: {
                    assetIndex: index,
                    totalAssets: res.assets.length,
                    isVideo: isVideo({ uri: asset.uri }),
                    fileSize,
                    resizeSize,
                  },
                });

                safeNotify({
                  type: "ImagePickerError: AssetProcessing",
                  searchableType: "iOS_IMAGE_UPLOAD_ERROR_AssetProcessing",
                  ...errorDetails,
                });
              } catch (notificationError) {
                // Slack通知が失敗しても元のエラー処理を継続
                // eslint-disable-next-line no-console
                console.warn(
                  "AssetProcessing notification failed:",
                  notificationError
                );
              }

              throw error; // Re-throw to stop processing
            }

            // 処理間に短い待機時間を設けてファイルシステムの安定性を確保
            if (index < res.assets.length - 1) {
              // eslint-disable-next-line no-await-in-loop
              await new Promise<void>((resolve) => {
                setTimeout(resolve, 50);
              });
            }
          }

          // eslint-disable-next-line no-console
          console.log("Sequential processing completed successfully");
        } catch (sequentialError) {
          // 逐次処理が失敗した場合もエラーをそのまま投げる
          // この catch は逐次処理全体の保護のため
          // eslint-disable-next-line no-console
          console.warn(
            "Sequential processing encountered issues, but continuing:",
            sequentialError
          );
          throw sequentialError;
        }

        // 画像ファイルリストも逐次処理で作成（安全に実行）
        const imageFileList: { uri: string; file: File }[] = [];

        try {
          // eslint-disable-next-line no-await-in-loop
          for (let index = 0; index < resizeUriList.length; index += 1) {
            const uri = resizeUriList[index];

            // eslint-disable-next-line no-console
            console.log(
              `Creating file object ${index + 1}/${
                resizeUriList.length
              } for URI: ${uri}`
            );

            try {
              // Web環境ではBlobに変換、React Native環境ではURIベースで処理
              if (Platform.OS === "web") {
                // eslint-disable-next-line no-console
                console.log(`Fetching blob for web platform: ${uri}`);

                // eslint-disable-next-line no-await-in-loop
                let blob: Blob;

                // data URIの場合は同期的変換、blob URLの場合はfetchBlobWithFallbackを使用
                if (uri.startsWith("data:")) {
                  // eslint-disable-next-line no-console
                  console.log(
                    "Detected data URI, using synchronous conversion"
                  );
                  blob = dataURItoBlob(uri);
                } else {
                  // blob URL等の場合、fetch()を試行し、失敗時はXMLHttpRequestにフォールバック
                  // eslint-disable-next-line no-await-in-loop
                  blob = await fetchBlobWithFallback(uri);
                }

                const extension = getExtensionFromType(blob.type);
                const fileName = `${Date.now()}_${index}.${extension}`;
                const file = new File([blob], fileName, { type: blob.type });

                // eslint-disable-next-line no-console
                console.log(`Web file created:`, {
                  name: fileName,
                  type: file.type,
                  size: file.size,
                });

                if (!isSupportedMimeType(file.type)) {
                  // MIMEタイプエラーログ
                  safeNotify(
                    {
                      type: "ImagePickerError: UnsupportedMimeType",
                      searchableType: "IMAGE_UPLOAD_ERROR_UnsupportedMime",
                      context: {
                        platform: Platform.OS,
                        fileInfo: {
                          actualMimeType: file.type,
                          fileName: file.name,
                          size: file.size,
                          sizeMB: Number(
                            (file.size / (1024 * 1024)).toFixed(2)
                          ),
                        },
                        processingInfo: {
                          fileIndex: index,
                          totalFiles: resizeUriList.length,
                        },
                        validation: {
                          pickupMediaType,
                          supportedTypes: supportMimeTypes(pickupMediaType),
                        },
                        deviceInfo: {
                          appVersion,
                          osVersion: Device.osVersion,
                        },
                      },
                      timestamp: new Date().toISOString(),
                    },
                    { level: "warning" }
                  );

                  throw new Error(
                    `選択可能なファイルは${supportMimeTypes(
                      pickupMediaType
                    ).join(", ")}のいずれかです。`
                  );
                }

                imageFileList.push({ uri, file });
              } else {
                // React Native環境: フォールバック機構付きでファイル作成
                // eslint-disable-next-line no-console
                console.log(
                  `Creating RN file object with fallback for URI: ${uri}`
                );

                // eslint-disable-next-line no-await-in-loop
                const fileWithFallback = await createRNFileWithFallback(
                  uri,
                  index
                );
                imageFileList.push(fileWithFallback);

                // eslint-disable-next-line no-console
                console.log(`RN file created with fallback:`, {
                  name: fileWithFallback.file.name,
                  type: fileWithFallback.file.type,
                  uriLength: fileWithFallback.uri.length,
                });
              }
            } catch (error) {
              // eslint-disable-next-line no-console
              console.error(`Error creating file object for ${uri}:`, error);

              try {
                // 詳細なSlack通知（失敗してもエラー処理は継続）
                const errorDetails = collectErrorDetails(error, {
                  operation: "FileCreation",
                  additionalInfo: {
                    uri:
                      uri !== undefined && uri !== ""
                        ? `${uri.substring(0, 50)}...`
                        : undefined,
                    platform: Platform.OS,
                    fileIndex: index,
                    totalFiles: resizeUriList.length,
                    isVideo: isVideo({ uri }),
                  },
                });

                safeNotify({
                  type: "ImagePickerError: FileCreation",
                  searchableType: "iOS_IMAGE_UPLOAD_ERROR_FileCreation",
                  ...errorDetails,
                });
              } catch (notificationError) {
                // Slack通知が失敗しても元のエラー処理を継続
                // eslint-disable-next-line no-console
                console.warn(
                  "FileCreation notification failed:",
                  notificationError
                );
              }

              throw new Error(
                `ファイル処理中にエラーが発生しました: ${
                  resolveError(error).message
                }`
              );
            }

            // 処理間に短い待機時間を設ける
            if (index < resizeUriList.length - 1) {
              // eslint-disable-next-line no-await-in-loop
              await new Promise<void>((resolve) => {
                setTimeout(resolve, 30);
              });
            }
          }

          // eslint-disable-next-line no-console
          console.log("File creation processing completed successfully");
        } catch (fileCreationError) {
          // ファイル作成処理が失敗した場合もエラーをそのまま投げる
          // この catch はファイル作成処理全体の保護のため
          // eslint-disable-next-line no-console
          console.warn(
            "File creation processing encountered issues:",
            fileCreationError
          );
          throw fileCreationError;
        }

        // 成功ログ（複数画像選択）
        safeNotify(
          {
            type: "ImagePickerSuccess: MultiImagePick",
            searchableType: "IMAGE_UPLOAD_SUCCESS_Multi",
            context: {
              platform: Platform.OS,
              filesInfo: {
                totalFiles: imageFileList.length,
                files: imageFileList.map((item, idx) => ({
                  index: idx,
                  size: item.file.size,
                  sizeMB: Number((item.file.size / (1024 * 1024)).toFixed(2)),
                  type: item.file.type,
                  name: item.file.name,
                })),
              },
              processing: {
                totalAssets: res.assets.length,
                successfullyProcessed: imageFileList.length,
              },
              deviceInfo: {
                appVersion,
                osVersion: Device.osVersion,
                modelName: Device.modelName,
              },
            },
            timestamp: new Date().toISOString(),
          },
          { level: "info" }
        );

        onChange(imageFileList);
      } catch (e: unknown) {
        try {
          // Slack通知（失敗しても既存エラーハンドリングは継続）
          const errorDetails = collectErrorDetails(e, {
            operation: "MultiImagePick",
            additionalInfo: {
              pickupMediaType,
              maxImageSize,
              maxVideoSize,
              resizeSize,
              selectionLimit,
              allowsMultipleSelection,
            },
          });

          safeNotify({
            type: "ImagePickerError: MultiImagePick",
            searchableType: "iOS_IMAGE_UPLOAD_ERROR_MultiImagePick",
            ...errorDetails,
          });
        } catch (notificationError) {
          // Slack通知が失敗しても既存動作を継続
          // eslint-disable-next-line no-console
          console.warn(
            "MultiImagePick notification failed:",
            notificationError
          );
        }

        onError(resolveError(e).message);
      } finally {
        onPickEnd();
      }
    }, [
      allowsMultipleSelection,
      maxImageSize,
      maxVideoSize,
      onChange,
      onError,
      onPickEnd,
      onPickStart,
      pickupImage,
      pickupMediaType,
      resize,
      resizeSize,
      selectionLimit,
    ]);

    // Web専用のファイル選択処理（HTML5標準のinput要素を使用）
    const handleWebFilePick = useCallback(
      async (event: React.ChangeEvent<HTMLInputElement>) => {
        const { files } = event.target;

        // ファイルが選択されていない場合の処理
        if (files === null || files.length === 0) {
          safeNotify(
            {
              type: "ImagePickerInfo: WebFilePickCanceled",
              searchableType: "IMAGE_UPLOAD_CANCELED_Web",
              context: {
                platform: "web",
                pickupMediaType,
                allowsMultipleSelection,
                deviceInfo: {
                  appVersion,
                  osVersion: Device.osVersion,
                },
              },
              timestamp: new Date().toISOString(),
            },
            { level: "info" }
          );
          onPickEnd();
          return;
        }

        // Web専用のコアロジックに委譲
        await handleWebFilePickCore({
          files,
          allowsMultipleSelection,
          pickupMediaType,
          maxImageSize,
          maxVideoSize,
          resizeSize,
          onPickStart,
          onPickEnd,
          onChange,
          onError,
          fileInputRef,
        });
      },
      [
        allowsMultipleSelection,
        maxImageSize,
        maxVideoSize,
        onChange,
        onError,
        onPickEnd,
        onPickStart,
        pickupMediaType,
        resizeSize,
      ]
    );

    useEffect(() => {
      if (processing) {
        if (loading) {
          return;
        }
        // Web版では input[type="file"] を使用するため、ここでは何もしない
        if (Platform.OS === "web") {
          setProcessing(false);
          return;
        }
        // iOS/Androidのみ既存のexpo-image-pickerを使用
        onPickStart();
        if (allowsMultipleSelection) {
          multiPick();
        } else {
          pick();
        }
      }
      setProcessing(false);
    }, [
      allowsMultipleSelection,
      loading,
      multiPick,
      onPickStart,
      pick,
      processing,
    ]);

    // Web版用のaccept属性を生成
    // NOTE: video/*だけではMOVファイルが表示されないブラウザがあるため、
    // 明示的に.mov拡張子とMIMEタイプを追加
    const getAcceptAttribute = () => {
      switch (pickupMediaType) {
        case ExpoImagePicker.MediaTypeOptions.Images:
          return "image/jpeg,image/png,image/gif,.jpg,.jpeg,.png,.gif";
        case ExpoImagePicker.MediaTypeOptions.Videos:
          return "video/mp4,video/quicktime,video/x-quicktime,.mp4,.mov,.MOV";
        case ExpoImagePicker.MediaTypeOptions.All:
        default:
          return "image/jpeg,image/png,image/gif,video/mp4,video/quicktime,video/x-quicktime,.jpg,.jpeg,.png,.gif,.mp4,.mov,.MOV";
      }
    };

    return (
      <>
        {Platform.OS === "web" && (
          <input
            ref={fileInputRef}
            accept={getAcceptAttribute()}
            multiple={allowsMultipleSelection}
            onChange={handleWebFilePick}
            style={{ display: "none" }}
            type="file"
          />
        )}
        <TouchableOpacity
          // @ts-expect-error プラットフォームの差異により型の不整合が起きるためignore
          ref={ref}
          onPress={() => {
            if (Platform.OS === "web") {
              // Web版ではinput要素をクリック
              try {
                fileInputRef.current?.click();
              } catch (error) {
                // ファイル選択ダイアログ起動失敗のログ
                safeNotify(
                  {
                    type: "ImagePickerError: FileDialogLaunchFailed",
                    searchableType: "FILE_DIALOG_LAUNCH_FAILED_Web",
                    context: {
                      platform: "web",
                      error: String(error),
                      pickupMediaType,
                      deviceInfo: {
                        appVersion,
                        osVersion: Device.osVersion,
                        modelName: Device.modelName,
                      },
                    },
                  },
                  { level: "error" }
                );
                onError("ファイル選択ダイアログを開けませんでした");
              }
            } else {
              // iOS/Androidでは既存の処理
              setProcessing(true);
            }
          }}
          style={styles.container}
        >
          {loading ? (
            <Loading color={Colors.white} />
          ) : (
            <Icon color={Colors.white} name="camera" size={30} />
          )}
        </TouchableOpacity>
      </>
    );
  }
);

const styles = StyleSheet.create({
  container: {
    width: "100%",
    height: "100%",
    alignItems: "center",
    justifyContent: "center",
  },
});
export default ImagePicker;
